import { Link } from "react-router-dom";
import { Button } from "../components";
import { usePageTitle } from "../hooks";
import { useEffect, useState } from "react";

/**
 * NotFoundPage - A magical, whimsical 404 page.
 *
 * Features a lost-in-the-forest theme where Prism the rainbow dragon
 * helps guide children back to the adventure. Designed to be fun and
 * encouraging rather than showing an "error" message.
 */
export function NotFoundPage() {
  usePageTitle("Oops! Wrong Turn");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-magic-800 via-magic-600 to-magic-400">
      {/* Animated stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <Star key={i} delay={i * 0.2} />
        ))}
      </div>

      {/* Floating clouds */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <Cloud className="absolute top-[10%] -left-20 w-48 h-24 opacity-20" delay={0} />
        <Cloud className="absolute top-[25%] right-0 w-64 h-32 opacity-15" delay={2} />
        <Cloud className="absolute top-[60%] -left-10 w-56 h-28 opacity-10" delay={4} />
      </div>

      {/* Ground with magical grass */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-meadow-700 via-meadow-500 to-transparent" />

      {/* Decorative mushrooms */}
      <div className="absolute bottom-4 left-[10%] animate-bounce" style={{ animationDuration: "3s" }}>
        <Mushroom color="fairy" />
      </div>
      <div className="absolute bottom-8 right-[15%] animate-bounce" style={{ animationDuration: "2.5s", animationDelay: "0.5s" }}>
        <Mushroom color="prism" />
      </div>
      <div className="absolute bottom-2 left-[60%] animate-bounce" style={{ animationDuration: "2.8s", animationDelay: "1s" }}>
        <Mushroom color="sunshine" />
      </div>

      {/* Main content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-12">
        {/* Prism the Dragon - floating animation */}
        <div
          className={`transition-all duration-1000 ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="relative animate-float">
            <PrismDragon />
            {/* Speech bubble */}
            <div className="absolute -top-4 -right-4 bg-cloud rounded-2xl px-4 py-2 shadow-lg transform rotate-3">
              <span className="text-body-lg font-display text-magic-600">?</span>
            </div>
          </div>
        </div>

        {/* 404 number with rainbow effect */}
        <div
          className={`mt-8 transition-all duration-1000 delay-300 ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h1 className="font-display font-extrabold text-[8rem] sm:text-[10rem] md:text-[12rem] leading-none text-transparent bg-clip-text bg-gradient-to-r from-prism-red via-prism-yellow via-prism-green via-prism-blue to-prism-purple drop-shadow-2xl">
            404
          </h1>
        </div>

        {/* Message card */}
        <div
          className={`mt-4 max-w-lg text-center transition-all duration-1000 delay-500 ease-out ${
            mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="bg-cloud/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border-4 border-sunshine-300">
            <h2 className="font-display font-bold text-display-sm text-magic-700 mb-3">
              Whoops! Wrong Turn!
            </h2>
            <p className="font-body text-body-lg text-neutral-600 mb-6 leading-relaxed">
              It looks like you've wandered into the enchanted forest!
              <br />
              <span className="text-magic-500 font-semibold">Prism</span> will help you find your way back to the adventure.
            </p>

            {/* Fun facts / encouraging message */}
            <div className="bg-gradient-to-r from-sunshine-100 to-fairy-100 rounded-2xl p-4 mb-6">
              <p className="font-body text-body-md text-neutral-700 italic">
                "Even the best explorers take wrong turns sometimes. That's how we discover new things!"
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button variant="primary" size="lg">
                  <span className="mr-2">
                    <HomeIcon />
                  </span>
                  Go Home
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Rainbow trail decoration at bottom */}
        <div
          className={`mt-8 transition-all duration-1000 delay-700 ease-out ${
            mounted ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
        >
          <RainbowTrail />
        </div>
      </div>

      {/* Floating particles */}
      <FloatingParticles />
    </div>
  );
}

/** Cute stylized Prism dragon illustration */
function PrismDragon() {
  return (
    <svg
      width="180"
      height="180"
      viewBox="0 0 180 180"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="drop-shadow-xl"
      aria-label="Prism the rainbow dragon looking curious"
    >
      {/* Body - gradient from rainbow colors */}
      <defs>
        <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF6B6B" />
          <stop offset="25%" stopColor="#FFB347" />
          <stop offset="50%" stopColor="#FFE66D" />
          <stop offset="75%" stopColor="#98D89B" />
          <stop offset="100%" stopColor="#74B9FF" />
        </linearGradient>
        <linearGradient id="wingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#A29BFE" />
          <stop offset="100%" stopColor="#74B9FF" />
        </linearGradient>
      </defs>

      {/* Wings */}
      <ellipse cx="50" cy="85" rx="30" ry="45" fill="url(#wingGradient)" opacity="0.9" />
      <ellipse cx="130" cy="85" rx="30" ry="45" fill="url(#wingGradient)" opacity="0.9" />

      {/* Body */}
      <ellipse cx="90" cy="100" rx="50" ry="45" fill="url(#bodyGradient)" />

      {/* Belly */}
      <ellipse cx="90" cy="110" rx="30" ry="25" fill="#FFF8E7" />

      {/* Tail curving up like a question mark */}
      <path
        d="M140 110 Q170 100 165 70 Q160 50 145 55"
        stroke="url(#bodyGradient)"
        strokeWidth="16"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="145" cy="55" r="10" fill="#A29BFE" />

      {/* Head */}
      <ellipse cx="90" cy="55" rx="40" ry="35" fill="url(#bodyGradient)" />

      {/* Horns */}
      <path d="M60 30 Q55 10 65 20" stroke="#FFE66D" strokeWidth="6" strokeLinecap="round" fill="none" />
      <path d="M120 30 Q125 10 115 20" stroke="#FFE66D" strokeWidth="6" strokeLinecap="round" fill="none" />

      {/* Ears */}
      <ellipse cx="55" cy="40" rx="10" ry="15" fill="#FF9EBC" />
      <ellipse cx="125" cy="40" rx="10" ry="15" fill="#FF9EBC" />

      {/* Snout */}
      <ellipse cx="90" cy="70" rx="20" ry="12" fill="#FFF8E7" />

      {/* Nostrils */}
      <circle cx="82" cy="68" r="3" fill="#E8C4A0" />
      <circle cx="98" cy="68" r="3" fill="#E8C4A0" />

      {/* Eyes - large and curious */}
      <ellipse cx="70" cy="50" rx="14" ry="16" fill="white" />
      <ellipse cx="110" cy="50" rx="14" ry="16" fill="white" />

      {/* Rainbow iris */}
      <defs>
        <linearGradient id="irisGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF6B6B" />
          <stop offset="50%" stopColor="#98D89B" />
          <stop offset="100%" stopColor="#74B9FF" />
        </linearGradient>
      </defs>
      <circle cx="72" cy="52" r="9" fill="url(#irisGradient)" />
      <circle cx="112" cy="52" r="9" fill="url(#irisGradient)" />

      {/* Pupils - looking to the side curiously */}
      <circle cx="74" cy="53" r="5" fill="#2D2438" />
      <circle cx="114" cy="53" r="5" fill="#2D2438" />

      {/* Eye highlights */}
      <circle cx="76" cy="50" r="3" fill="white" />
      <circle cx="116" cy="50" r="3" fill="white" />
      <circle cx="72" cy="55" r="1.5" fill="white" />
      <circle cx="112" cy="55" r="1.5" fill="white" />

      {/* Eyebrows - raised curiously */}
      <path d="M58 38 Q70 32 80 38" stroke="#C77DDB" strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M100 38 Q110 32 122 38" stroke="#C77DDB" strokeWidth="3" strokeLinecap="round" fill="none" />

      {/* Cheek blush */}
      <ellipse cx="55" cy="60" rx="8" ry="5" fill="#FFB8C8" opacity="0.7" />
      <ellipse cx="125" cy="60" rx="8" ry="5" fill="#FFB8C8" opacity="0.7" />

      {/* Small smile */}
      <path d="M80 78 Q90 85 100 78" stroke="#C77DDB" strokeWidth="3" strokeLinecap="round" fill="none" />

      {/* Front paws */}
      <ellipse cx="65" cy="140" rx="15" ry="12" fill="url(#bodyGradient)" />
      <ellipse cx="115" cy="140" rx="15" ry="12" fill="url(#bodyGradient)" />

      {/* Paw pads */}
      <circle cx="60" cy="145" r="4" fill="#FFF8E7" />
      <circle cx="70" cy="145" r="4" fill="#FFF8E7" />
      <circle cx="110" cy="145" r="4" fill="#FFF8E7" />
      <circle cx="120" cy="145" r="4" fill="#FFF8E7" />

      {/* Sparkles around dragon */}
      <g className="animate-pulse">
        <path d="M25 60 L30 65 L25 70 L20 65 Z" fill="#FFE66D" />
        <path d="M155 40 L160 45 L155 50 L150 45 Z" fill="#74B9FF" />
        <path d="M35 120 L40 125 L35 130 L30 125 Z" fill="#FF6B6B" />
      </g>
    </svg>
  );
}

/** Decorative mushroom */
function Mushroom({ color }: { color: "fairy" | "prism" | "sunshine" }) {
  const colors = {
    fairy: { cap: "#FF6B9D", spots: "#FFE4EC" },
    prism: { cap: "#74B9FF", spots: "#E3F2FD" },
    sunshine: { cap: "#FFD93D", spots: "#FFFDE6" },
  };

  return (
    <svg width="40" height="50" viewBox="0 0 40 50" fill="none" aria-hidden="true">
      {/* Stem */}
      <rect x="14" y="25" width="12" height="20" rx="4" fill="#FFF8E7" />
      {/* Cap */}
      <ellipse cx="20" cy="22" rx="18" ry="14" fill={colors[color].cap} />
      {/* Spots */}
      <circle cx="12" cy="18" r="4" fill={colors[color].spots} />
      <circle cx="24" cy="15" r="3" fill={colors[color].spots} />
      <circle cx="28" cy="22" r="2.5" fill={colors[color].spots} />
    </svg>
  );
}

/** Rainbow trail decoration */
function RainbowTrail() {
  return (
    <svg width="200" height="30" viewBox="0 0 200 30" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="rainbowTrail" x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#FF6B6B" stopOpacity="0.8" />
          <stop offset="20%" stopColor="#FFB347" stopOpacity="0.8" />
          <stop offset="40%" stopColor="#FFE66D" stopOpacity="0.8" />
          <stop offset="60%" stopColor="#98D89B" stopOpacity="0.8" />
          <stop offset="80%" stopColor="#74B9FF" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#A29BFE" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      <path
        d="M0 15 Q50 5 100 15 Q150 25 200 15"
        stroke="url(#rainbowTrail)"
        strokeWidth="8"
        strokeLinecap="round"
        fill="none"
        className="animate-pulse"
      />
    </svg>
  );
}

/** Animated star component */
function Star({ delay }: { delay: number }) {
  const size = 4 + Math.random() * 8;
  const left = Math.random() * 100;
  const top = Math.random() * 60;
  const duration = 2 + Math.random() * 3;

  return (
    <div
      className="absolute animate-twinkle"
      style={{
        left: `${left}%`,
        top: `${top}%`,
        width: size,
        height: size,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
    >
      <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path d="M5 0 L6 4 L10 5 L6 6 L5 10 L4 6 L0 5 L4 4 Z" fill="#FFE66D" />
      </svg>
    </div>
  );
}

/** Cloud shape */
function Cloud({ className, delay }: { className?: string; delay: number }) {
  return (
    <div
      className={`${className} animate-float-slow`}
      style={{ animationDelay: `${delay}s` }}
    >
      <svg viewBox="0 0 100 50" fill="none" className="w-full h-full" aria-hidden="true">
        <ellipse cx="30" cy="35" rx="25" ry="15" fill="white" />
        <ellipse cx="50" cy="25" rx="30" ry="20" fill="white" />
        <ellipse cx="75" cy="35" rx="20" ry="12" fill="white" />
      </svg>
    </div>
  );
}

/** Floating magical particles */
function FloatingParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => {
        const colors = ["#FF6B6B", "#FFB347", "#FFE66D", "#98D89B", "#74B9FF", "#A29BFE"];
        const color = colors[i % colors.length];
        const size = 6 + Math.random() * 10;
        const left = Math.random() * 100;
        const delay = Math.random() * 5;
        const duration = 5 + Math.random() * 5;

        return (
          <div
            key={i}
            className="absolute rounded-full animate-rise"
            style={{
              left: `${left}%`,
              bottom: "-20px",
              width: size,
              height: size,
              backgroundColor: color,
              opacity: 0.6,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
            }}
          />
        );
      })}
    </div>
  );
}

/** Home icon */
function HomeIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9,22 9,12 15,12 15,22" />
    </svg>
  );
}

export default NotFoundPage;
